import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import * as refOtdFilialsApi from '@/api/refOtdFilials'
import type { FilialBindingFilters, BulkUpdateRequest, CurrentStructureFilters } from '@/types/refOtdFilials'
import { useOrgScopedQueryKey } from '@/hooks/useOrgScopedQueryKey'
import { getApiErrorDetail } from '@/utils/apiError'

export const usePeriods = () => {
  const orgContextKey = useOrgScopedQueryKey()
  return useQuery({
    queryKey: ['otd-filials-periods', orgContextKey],
    queryFn: () => refOtdFilialsApi.getPeriods(),
  })
}

export const useCheckPeriod = (period: string | null) => {
  const orgContextKey = useOrgScopedQueryKey()
  return useQuery({
    queryKey: ['otd-filials-period-check', orgContextKey, period],
    queryFn: () => refOtdFilialsApi.checkPeriod(period!),
    enabled: !!period,
  })
}

export const useOtdFilials = (filters: FilialBindingFilters) => {
  const orgContextKey = useOrgScopedQueryKey()
  return useQuery({
    queryKey: ['otd-filials', orgContextKey, filters],
    queryFn: () => refOtdFilialsApi.getOtdFilials(filters),
    enabled: !!filters.period, // Only fetch if period is selected
  })
}

export const useUpdateFilialBinding = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id_otd, period, fil_code }: { id_otd: string; period: string; fil_code: number | null }) =>
      refOtdFilialsApi.updateFilialBinding(id_otd, period, fil_code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['otd-filials'] })
      message.success('Филиал обновлён')
    },
    onError: (error: unknown) => {
      message.error(getApiErrorDetail(error) || 'Ошибка при обновлении филиала')
    },
  })
}

export const useUpdateBinding = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id_otd, period, data }: { id_otd: string; period: string; data: refOtdFilialsApi.BindingUpdate }) =>
      refOtdFilialsApi.updateBinding(id_otd, period, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['otd-filials'] })
    },
    onError: (error: unknown) => {
      message.error(getApiErrorDetail(error) || 'Ошибка при обновлении')
    },
  })
}

export const useBulkUpdateFilials = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (request: BulkUpdateRequest) =>
      refOtdFilialsApi.bulkUpdateFilials(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['otd-filials'] })
      message.success('Филиалы обновлены')
    },
    onError: (error: unknown) => {
      message.error(getApiErrorDetail(error) || 'Ошибка при массовом обновлении')
    },
  })
}

export const useCurrentStructure = (filters: CurrentStructureFilters) => {
  const orgContextKey = useOrgScopedQueryKey()
  return useQuery({
    queryKey: ['current-structure', orgContextKey, filters],
    queryFn: () => refOtdFilialsApi.getCurrentStructure(filters),
  })
}
